import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.tsx'
import {
  isSystemAdmin,
  isAgencyAdmin,
  isCompanyOnlyUser,
  getFirstCompanyId,
} from '../utils/roles.ts'
import { User, ChevronDown, Settings, Users, Building2, Eye } from 'lucide-react'
import ImpersonationPicker from './ImpersonationPicker.tsx'

const NavBar: React.FC = () => {
  const location = useLocation()
  const { user, logout, loading, impersonation, stopImpersonating } = useAuth()
  const navigate = useNavigate()
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false)
  const [agencyDropdownOpen, setAgencyDropdownOpen] = useState(false)
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)

  // Hide NavBar on shared/public report pages
  if (location.pathname.startsWith('/shared/')) {
    return null
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const showAdminMenu = isSystemAdmin(user)
  const showAgencyMenu = isAgencyAdmin(user)

  const handleStopImpersonating = async () => {
    try {
      await stopImpersonating()
      navigate('/')
    } catch (error) {
      console.error('Failed to stop impersonating:', error)
    }
  }

  return (
    <>
      {impersonation?.active && (
        <div className="bg-amber-500 text-white text-sm">
          <div className="container mx-auto px-4 py-2 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Eye size={16} />
              Viewing as <strong>{user?.name || user?.email}</strong>
              {impersonation.admin && (
                <span className="opacity-80">(as {impersonation.admin.name})</span>
              )}
            </span>
            <button
              onClick={handleStopImpersonating}
              className="px-3 py-1 rounded bg-white/20 hover:bg-white/30 transition-colors"
            >
              Return to admin
            </button>
          </div>
        </div>
      )}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="text-xl font-bold text-primary-700 hover:text-primary-800 transition-colors"
          >
            Marzz AI
          </Link>

          <div className="flex items-center gap-4">
            {!loading && !user && (
              <Link
                to="/login"
                className="text-primary-700 hover:text-primary-800 hover:underline transition-colors"
              >
                Login
              </Link>
            )}

            {!loading && user && (
              <>
                <div className="flex items-center gap-6">
                  <Link
                    to="/reports"
                    className="text-gray-700 hover:text-primary-700 hover:underline transition-colors"
                  >
                    Reports
                  </Link>
                  {isCompanyOnlyUser(user) ? (
                    <Link
                      to={`/company/${getFirstCompanyId(user)}`}
                      className="text-gray-700 hover:text-primary-700 hover:underline transition-colors"
                    >
                      Dashboard
                    </Link>
                  ) : (
                    <Link
                      to="/companies"
                      className="text-gray-700 hover:text-primary-700 hover:underline transition-colors"
                    >
                      Companies
                    </Link>
                  )}

                  {/* Admin Dropdown */}
                  {showAdminMenu && (
                    <div className="relative">
                      <button
                        onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                        className="flex items-center gap-2 text-gray-700 hover:text-primary-700 hover:underline transition-colors"
                      >
                        <Settings size={16} />
                        Admin
                        <ChevronDown size={14} />
                      </button>
                      {adminDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                          <div className="py-2">
                            <Link
                              to="/admin/users"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              onClick={() => setAdminDropdownOpen(false)}
                            >
                              Users
                            </Link>
                            <Link
                              to="/admin/agency-creation"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              onClick={() => setAdminDropdownOpen(false)}
                            >
                              Agency Creation
                            </Link>
                            <Link
                              to="/admin/agency-company-connection"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              onClick={() => setAdminDropdownOpen(false)}
                            >
                              Agency-Company Connection
                            </Link>
                            <Link
                              to="/admin/llm-prompts"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              onClick={() => setAdminDropdownOpen(false)}
                            >
                              LLM Prompts
                            </Link>
                            {showAgencyMenu && (
                              <Link
                                to="/agency/user-management"
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                onClick={() => setAdminDropdownOpen(false)}
                              >
                                Manage Agency Users
                              </Link>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* "Log in as" picker — system admins only, hidden mid-impersonation */}
                  {showAdminMenu && !impersonation?.active && <ImpersonationPicker />}

                  {/* Agency Dropdown */}
                  {showAgencyMenu && (
                    <div className="relative">
                      <button
                        onClick={() => setAgencyDropdownOpen(!agencyDropdownOpen)}
                        className="flex items-center gap-2 text-gray-700 hover:text-primary-700 hover:underline transition-colors"
                      >
                        <Building2 size={16} />
                        Agency
                        <ChevronDown size={14} />
                      </button>
                      {agencyDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                          <div className="py-2">
                            <Link
                              to="/agency/user-management"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              onClick={() => setAgencyDropdownOpen(false)}
                            >
                              <Users size={14} className="inline mr-2" />
                              Manage Users
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="relative flex items-center border-l border-gray-200 pl-4">
                  <button
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <User size={16} />
                    <span className="hidden sm:inline">
                      {user.name && user.name !== ''
                        ? (() => {
                            const nameParts = user.name.split(', ')
                            if (nameParts.length === 2) {
                              return `${nameParts[1]} ${nameParts[0]}`
                            }
                            return user.name
                          })()
                        : user.email}
                    </span>
                    <ChevronDown size={14} />
                  </button>
                  {profileDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      <div className="py-2">
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setProfileDropdownOpen(false)}
                        >
                          Edit Profile
                        </Link>
                        <button
                          onClick={() => {
                            setProfileDropdownOpen(false)
                            handleLogout()
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Click outside to close dropdowns */}
        {(adminDropdownOpen || agencyDropdownOpen || profileDropdownOpen) && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setAdminDropdownOpen(false)
              setAgencyDropdownOpen(false)
              setProfileDropdownOpen(false)
            }}
          />
        )}
      </nav>
    </>
  )
}

export default NavBar
